import { useTranslation } from "react-i18next";

import ArrowIcon from "src/core/assets/images/icons/arrow.svg?react";
import DownloadDocumentIcon from "src/core/assets/images/icons/downloadDocument.svg?react";
import HomeIcon from "src/core/assets/images/icons/home.svg?react";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { ExportPdfFolderAction } from "src/core/components/folder/ExportPdfFolderAction";
import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { useValidation } from "src/core/hooks/folder/useValidation";

type AccepterModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  actionType: "accept" | "refuse";
  isModulationInProgress: boolean;
  onAfterRefetch?: () => void;
};

export const AccepterModal = ({
  open,
  setOpen,
  actionType,
  isModulationInProgress,
  onAfterRefetch,
}: AccepterModalProps) => {
  const { t } = useTranslation();
  const { handleValidation, handleCancel, isLoading, isValidated, handleRedirect } = useValidation(
    setOpen,
    onAfterRefetch
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleCancel();
      }}
    >
      <DialogContent hideCloseButton={!isValidated}>
        <DialogHeader>
          <DialogTitle>
            {t(actionType === "accept" ? "folder.accepter.title" : "folder.refuser.title")}
          </DialogTitle>
          <DialogDescription className="sr-only"></DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center whitespace-pre-line">
          {isLoading ? (
            <Spinner />
          ) : isValidated ? (
            <div className="flex flex-col items-center justify-center mb-20">
              <Button
                variant="card"
                onClick={handleRedirect}
                size="card"
                className="gap-x-15 mb-30"
              >
                <HomeIcon className="w-30 h-30" />
                {t("returnHome")}
                <ArrowIcon className="text-primary w-30 h-30 ml-50" />
              </Button>
              <ExportPdfFolderAction
                icon={<DownloadDocumentIcon />}
                label={t("folder.export.download")}
              />
            </div>
          ) : (
            <>
              <div className="flex flex-col whitespace-pre-line font-light">
                <p>
                  {t(
                    actionType === "accept" ? "folder.accepter.message" : "folder.refuser.message"
                  )}
                </p>

                {isModulationInProgress && (
                  <p className="text-red-medium mt-20">
                    {t("folder.accepter.modulationInProgressMessage")}
                  </p>
                )}
              </div>

              <DialogFooter className="gap-4">
                <Button
                  onClick={() => handleValidation(actionType)}
                  loading={isLoading}
                  disabled={isModulationInProgress}
                >
                  {t(actionType === "accept" ? "folder.accepter.button" : "folder.refuser.button")}
                </Button>
                <Button variant="link" onClick={handleCancel} disabled={isLoading}>
                  {t("global.form.cancel")}
                </Button>
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
